import React, { useState, useEffect, useRef } from 'react';
import { CompleteKundali, Language, ChartStyle, BirthDetails, MenuLayoutMode } from '../types/kundali';
import { UserProfile } from '../lib/firebase';
import { UserProfileRecord, UserRole } from '../types/user';
import { APP_VERSION } from '../types';
import { AdvancedLifeAnalysisResult, LifeThemeId } from '../types/advancedLifeAnalysis';
import { LIFE_THEMES_REGISTRY } from '../utils/advancedLifeAnalysisTaxonomy';
import { runAdvancedLifeAnalysis } from '../utils/advancedLifeAnalysisEngine';
import { computeLifeStageContext } from '../utils/lifeStageContextEngine';
import { formatDOB, formatTimeToAMPM, calculateAgeString } from '../utils/dateUtils';
import { getMenuLayoutModePref } from '../utils/layoutPreferences';
import { Header } from './Header';
import { JatakaProfileHeader } from './JatakaProfileHeader';
import { CollapsibleSidebar } from './CollapsibleSidebar';
import { JatakaBirthBlueprintModal } from './JatakaBirthBlueprintModal';
import { BasicAnalysisView } from './role/BasicAnalysisView';
import { ProAnalysisView } from './role/ProAnalysisView';
import { AcharyaConsultationView } from './role/AcharyaConsultationView';
import { ManagementWorkspace } from './management/ManagementWorkspace';
import { VedicMilestoneDestinyStudio } from './v520/VedicMilestoneDestinyStudio';
import { KundaliWorkbench } from './KundaliWorkbench';
import { ClassicalReasoningModal } from './ClassicalReasoningModal';
import { PrintReportModal } from './PrintReportModal';
import { SwastikLogo } from './SwastikLogo';
import { ProfileMenu } from './ProfileMenu';
import { SettingsModal } from './SettingsModal';
import { AppVersionModal } from './AppVersionModal';
import { BirthTimeRectificationModal } from './BirthTimeRectificationModal';
import { BtrPromptModal } from './BtrPromptModal';
import { useClickOutside } from '../hooks/useClickOutside';
import { isBasicTierUser, isSuperAdminUser } from '../utils/rbac';
import {
  Sparkles,
  Award,
  Compass,
  GraduationCap,
  Briefcase,
  Layers,
  Printer,
  RotateCcw,
  PlusCircle,
  FolderOpen,
  Shield,
  Clock,
  Calendar,
  ChevronRight,
  ArrowLeft,
  Settings,
  HelpCircle,
  CheckCircle2,
  ExternalLink,
  ChevronDown,
  User,
  Edit3,
  Wrench,
  Target,
  ScrollText
} from 'lucide-react';

export type DashboardRole = 'BASIC' | 'PRO' | 'ACHARYA' | 'MANAGEMENT';
export type V515Role = DashboardRole; // Backwards compatibility alias

interface NewProfessionalDashboardProps {
  kundali: CompleteKundali;
  lang: Language;
  setLang: (l: Language) => void;
  chartStyle: ChartStyle;
  setChartStyle: (s: ChartStyle) => void;
  currentUser: UserProfile | null;
  savedKundalis: CompleteKundali[];
  activeTab?: string;
  onLoadSavedKundali: (k: CompleteKundali) => void;
  onApplyRectifiedKundali?: (rectifiedKundali: CompleteKundali) => void;
  onOpenBirthForm: () => void;
  onEditKundali?: () => void;
  onOpenMyVault: () => void;
  onOpenAuth: () => void;
  onOpenLegacyDashboard: (targetRole?: DashboardRole) => void;
  onOpenGoldenRegression?: () => void;
  onOpenAdminPortal?: () => void;
  onOpenWhatsAppModal?: () => void;
  onOpenBackupRestore?: () => void;
  onLogout?: () => void;
  onSwitchRole?: (role: UserRole) => void;
  aiQuota?: { remaining: number; allowance: number; resetDate?: string } | null;
  customQuestionInput?: string;
  onCustomQuestionChange?: (val: string) => void;
  onRequestAiAnalysis?: () => void;
  isAiLoading?: boolean;
  aiInterpretation?: string;
  aiError?: string;
}

export const NewProfessionalDashboard: React.FC<NewProfessionalDashboardProps> = ({
  kundali,
  lang,
  setLang,
  chartStyle,
  setChartStyle,
  currentUser,
  savedKundalis,
  activeTab,
  onLoadSavedKundali,
  onApplyRectifiedKundali,
  onOpenBirthForm,
  onEditKundali,
  onOpenMyVault,
  onOpenAuth,
  onOpenLegacyDashboard,
  onOpenGoldenRegression,
  onOpenAdminPortal,
  onOpenWhatsAppModal,
  onOpenBackupRestore,
  onLogout,
  onSwitchRole,
  aiQuota = null,
  customQuestionInput = '',
  onCustomQuestionChange = () => {},
  onRequestAiAnalysis = () => {},
  isAiLoading = false,
  aiInterpretation = '',
  aiError = ''
}) => {
  const isHi = lang === 'hi';
  const isBasicUser = isBasicTierUser(currentUser);
  const isSuperAdmin = isSuperAdminUser(currentUser);

  // Active Purpose-Built Role Experience: Locked to BASIC for basic users
  const [activeRole, setActiveRole] = useState<DashboardRole>(() => {
    if (isBasicTierUser(currentUser)) {
      return 'BASIC';
    }
    try {
      const savedRole = localStorage.getItem('vj_active_role') || localStorage.getItem('v515_active_role');
      if (savedRole && ['BASIC', 'PRO', 'ACHARYA', 'MANAGEMENT'].includes(savedRole)) {
        return savedRole as DashboardRole;
      }
    } catch {}
    return 'PRO';
  });

  // Strict RBAC Enforcement: Basic users can only access BASIC profile
  useEffect(() => {
    if (isBasicUser && activeRole !== 'BASIC') {
      setActiveRole('BASIC');
    }
  }, [isBasicUser, activeRole]);

  // Profile Menu Dropdown State & Ref
  const [showProfileMenu, setShowProfileMenu] = useState<boolean>(false);
  const profileContainerRef = useRef<HTMLDivElement | null>(null);

  // Settings Modal State (Universal across all roles: Language, Chart Style, Legacy, Ephemeris)
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState<boolean>(false);
  const [isVersionModalOpen, setIsVersionModalOpen] = useState<boolean>(false);
  const [isRectificationModalOpen, setIsRectificationModalOpen] = useState<boolean>(false);
  const [isBtrPromptOpen, setIsBtrPromptOpen] = useState<boolean>(false);
  const [isSidebarOpenOnMobile, setIsSidebarOpenOnMobile] = useState<boolean>(false);
  const [showBlueprintModal, setShowBlueprintModal] = useState<boolean>(false);
  
  // Workstation Menu & Navigation Layout Mode: Defaults to 'horizontal'
  const [menuLayoutMode, setMenuLayoutMode] = useState<MenuLayoutMode>(getMenuLayoutModePref);

  useEffect(() => {
    const handleMenuLayoutChange = (e: Event) => {
      const mode = (e as CustomEvent<MenuLayoutMode>).detail;
      if (mode === 'horizontal' || mode === 'vertical') {
        setMenuLayoutMode(mode);
      }
    };
    window.addEventListener('astro_menu_layout_changed', handleMenuLayoutChange);
    return () => window.removeEventListener('astro_menu_layout_changed', handleMenuLayoutChange);
  }, []);

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('astro_sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });

  const toggleSidebarCollapse = () => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('astro_sidebar_collapsed', String(next));
      } catch {}
      return next;
    });
  };

  // Global Outside-Click & Escape Dismissal for Profile Dropdown
  useClickOutside(profileContainerRef, showProfileMenu, () => setShowProfileMenu(false));

  const toggleProfileMenu = () => {
    setShowProfileMenu((prev) => !prev);
  };

  // Persist role selection
  useEffect(() => {
    try {
      localStorage.setItem('vj_active_role', activeRole);
    } catch {}
  }, [activeRole]);

  // Selected Life Theme & Target Question
  const selectedThemeIdState = useState<LifeThemeId>('career_job');
  const selectedThemeId = selectedThemeIdState[0];
  const setSelectedThemeId = selectedThemeIdState[1];
  const [selectedQuestionId, setSelectedQuestionId] = useState<string>('q_career_growth');
  const [proWorkspaceTab, setProWorkspaceTab] = useState<'enterprise-studio' | 'astrologer' | 'tools' | 'synthesis' | 'dossier'>('enterprise-studio');

  // Global tab navigation event listener for deep linking / gemstone advisor
  useEffect(() => {
    const handleNav = (e: Event) => {
      const detail = (e as CustomEvent<string>).detail;
      if (detail === 'gemstones') {
        setActiveRole('PRO');
        setProWorkspaceTab('tools');
        window.scrollTo({ top: 120, behavior: 'smooth' });
      }
    };
    window.addEventListener('astro_navigate_tab', handleNav);
    return () => window.removeEventListener('astro_navigate_tab', handleNav);
  }, []);

  useEffect(() => {
    if (activeTab === 'gemstones') {
      setActiveRole('PRO');
      setProWorkspaceTab('tools');
    }
  }, [activeTab]);

  // Modal States
  const [isReasoningModalOpen, setIsReasoningModalOpen] = useState<boolean>(false);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState<boolean>(false);
  const [printReportMode, setPrintReportMode] = useState<'BASIC' | 'LEARNER' | 'PRO' | 'ACHARYA'>('PRO');

  // Derived Life Stage Context from Invariant Vedic Calculations
  const lifeStageContext = React.useMemo(() => computeLifeStageContext(kundali), [kundali]);
  const diffYears = lifeStageContext.years;
  const diffMonths = lifeStageContext.months;
  const ashrama = lifeStageContext.stage;
  const purushartha = { focusEn: lifeStageContext.stage.developmentalFocusEn, focusHi: lifeStageContext.stage.developmentalFocusHi };

  // Calculate Deterministic Advanced Life Analysis
  const analysisResult: AdvancedLifeAnalysisResult = React.useMemo(() => {
    return runAdvancedLifeAnalysis(kundali, selectedThemeId, selectedQuestionId);
  }, [kundali, selectedThemeId, selectedQuestionId]);

  // Current Mahadasha & Antardasha
  const currentDasha = React.useMemo(() => {
    const now = new Date();
    if (kundali.dashas && kundali.dashas.length > 0) {
      const active = kundali.dashas.find(d => {
        const start = new Date(d.startDate).getTime();
        const end = new Date(d.endDate).getTime();
        const current = now.getTime();
        return current >= start && current <= end;
      }) || kundali.dashas[0];

      return {
        mahadasha: active.planet,
        antardasha: ((active as any).subDashas?.[0]?.planet || active.planet),
        endYear: new Date(active.endDate).getFullYear()
      };
    }
    return { mahadasha: 'Jupiter', antardasha: 'Saturn', endYear: 2029 };
  }, [kundali]);

  // Current Key Gochar
  const keyGochar = React.useMemo(() => {
    return {
      jupiter: isHi ? 'वृषभ (अनुकूल)' : 'Taurus (Supportive)',
      saturn: isHi ? 'कुंभ (शश योग)' : 'Aquarius (Shasha Yoga)',
      rahuKetu: isHi ? 'मीन/कन्या (सक्रिय)' : 'Pisces/Virgo (Active Axis)'
    };
  }, [isHi]);

  const handleOpenPrintReport = (mode: 'BASIC' | 'LEARNER' | 'PRO' | 'ACHARYA' = 'PRO') => {
    setPrintReportMode(mode);
    setIsPrintModalOpen(true);
  };

  const handleDashboardRoleSwitch = (newRole: UserRole) => {
    if (newRole === UserRole.BASIC_USER) {
      setActiveRole('BASIC');
    } else if (newRole === UserRole.PRO_ASTROLOGER || newRole === UserRole.ADVANCED_ASTROLOGY_USER) {
      setActiveRole('PRO');
    } else if (newRole === UserRole.CONSULTING_ASTROLOGER) {
      setActiveRole('ACHARYA');
    } else if (newRole === UserRole.ADMIN || newRole === UserRole.SUPER_ADMIN) {
      setActiveRole('MANAGEMENT');
    }
    if (onSwitchRole) {
      onSwitchRole(newRole);
    }
  };

  const clientName = kundali.birthDetails?.name || (isHi ? 'जातक कुंडली' : 'Native Horoscope');

  return (
    <div
      id="professional-workstation"
      className="min-h-screen bg-astro-bg text-astro-text flex flex-col selection:bg-astro-accent/20"
    >
      {/* ============================================================ */}
      {/* 1. GLOBAL HIGH-DENSITY NAVIGATION DOCK (7 ICONS)             */}
      {/* ============================================================ */}
      {/* 1. GLOBAL HIGH-DENSITY NAVIGATION DOCK                        */}
      {/* ============================================================ */}
      <Header
        chartStyle={chartStyle}
        setChartStyle={setChartStyle}
        lang={lang}
        setLang={setLang}
        kundali={kundali}
        savedKundalis={savedKundalis}
        onLoadSavedKundali={onLoadSavedKundali}
        onOpenBirthForm={onOpenBirthForm}
        onNewChart={onOpenBirthForm}
        onPrintReport={() => handleOpenPrintReport(activeRole === 'MANAGEMENT' ? 'PRO' : activeRole)}
        onPrint={() => handleOpenPrintReport(activeRole === 'MANAGEMENT' ? 'PRO' : activeRole)}
        onOpenVault={onOpenMyVault}
        onOpenMyVault={onOpenMyVault}
        onOpenBTR={() => setIsBtrPromptOpen(true)}
        onApplyRectifiedKundali={(rectified) => onLoadSavedKundali(rectified)}
        onOpenSettings={() => setIsSettingsModalOpen(true)}
        currentUser={currentUser}
        onOpenAuth={onOpenAuth}
        onOpenAdminPortal={onOpenAdminPortal}
        onOpenWhatsAppModal={onOpenWhatsAppModal}
        onOpenBackupRestore={onOpenBackupRestore}
        onOpenGoldenRegression={onOpenGoldenRegression}
        onLogout={onLogout}
        onSwitchRole={handleDashboardRoleSwitch}
        onToggleSidebar={menuLayoutMode === 'vertical' ? toggleSidebarCollapse : undefined}
        isSidebarOpen={menuLayoutMode === 'vertical' ? isSidebarOpenOnMobile : false}
        isSidebarCollapsed={isSidebarCollapsed}
        hideTopEssentials={menuLayoutMode === 'vertical'}
        hideRightDock={menuLayoutMode === 'vertical'}
        onOpenBlueprint={() => setShowBlueprintModal(true)}
        onEditKundali={onEditKundali || onOpenBirthForm}
        onOpenVersionModal={() => setIsVersionModalOpen(true)}
        menuLayoutMode={menuLayoutMode}
        onMenuLayoutModeChange={setMenuLayoutMode}
      />

      {/* ============================================================ */}
      {/* 2. SUB-HEADER STRIP: JATAKA PROFILE DOSSIER (ALL LAYOUT MODES) */}
      {/* ============================================================ */}
      {kundali && (
        <JatakaProfileHeader
          kundali={kundali}
          savedKundalis={savedKundalis}
          currentUser={currentUser}
          onLoadSavedKundali={onLoadSavedKundali}
          name={kundali.birthDetails?.name || 'Navya Gupta'}
          lagnaSign={kundali.houses?.[0]?.sign || 'Cancer'}
          lagnaNum={kundali.houses?.[0]?.signIndex !== undefined ? kundali.houses[0].signIndex + 1 : 4}
          moonSign={kundali.planets?.find((p) => p.name === 'Moon')?.sign || 'Scorpio'}
          moonNum={(() => {
            const moonP = kundali.planets?.find((p) => p.name === 'Moon');
            return moonP?.signIndex !== undefined ? moonP.signIndex + 1 : 8;
          })()}
          dob={kundali.birthDetails?.date ? formatDOB(kundali.birthDetails.date) : '12-Mar-2015'}
          tob={kundali.birthDetails?.time ? formatTimeToAMPM(kundali.birthDetails.time) : '14:13'}
          age={calculateAgeString(kundali.birthDetails?.date, lang) || '11y 5m'}
          place={kundali.birthDetails?.placeName || 'Delhi, India'}
          latitude={kundali.birthDetails?.latitude}
          longitude={kundali.birthDetails?.longitude}
          timezoneOffsetMinutes={kundali.birthDetails?.timezoneOffsetMinutes}
          dasha={(() => {
            const currentMaha = kundali.dashas?.find((d) => d.isCurrent);
            const currentAntar = currentMaha?.antardashas?.find((a) => a.isCurrent);
            return currentMaha ? `${currentMaha.planet.slice(0, 2)}-${currentAntar ? currentAntar.planet.slice(0, 2) : ''}` : 'Me-Ve';
          })()}
          workspaceName={
            activeRole === 'PRO'
              ? (proWorkspaceTab === 'enterprise-studio'
                  ? (isHi ? 'वैदिक डेस्टिनी स्टूडियो' : 'Vedic Destiny Studio')
                  : proWorkspaceTab === 'astrologer'
                  ? (isHi ? 'कुंडली कार्यपीठ' : 'Astrologers Workbench')
                  : proWorkspaceTab === 'tools'
                  ? (isHi ? 'ज्योतिषी उपकरण व एडवांस वर्कस्पेस' : 'Astrologers tools and workspace')
                  : proWorkspaceTab === 'synthesis'
                  ? (isHi ? 'लक्षित संश्लेषण एवं समय निर्धारण' : 'Targeted Synthesis & Timing')
                  : (isHi ? 'ज्योतिषी निदान डॉसियर' : "Astrologer's Diagnostic Dossier"))
              : activeRole === 'ACHARYA'
              ? (isHi ? 'आचार्य परामर्श कक्ष' : 'Acharya Consultation')
              : activeRole === 'BASIC'
              ? (isHi ? 'सरल कुंडली व भाव विवरण' : 'Basic Horoscope & House Insights')
              : (isHi ? 'प्रबंधन एवं ग्राहक कार्यक्षेत्र' : 'Management & Client Desk')
          }
          activeWorkspaceMode={proWorkspaceTab}
          onSelectWorkspaceMode={(mode) => {
            setActiveRole('PRO');
            setProWorkspaceTab(mode as any);
          }}
          onToggleWorkspace={() => {
            if (activeRole === 'PRO') {
              setProWorkspaceTab((prev) => (prev === 'enterprise-studio' ? 'astrologer' : prev === 'astrologer' ? 'tools' : 'enterprise-studio'));
            } else {
              setActiveRole('PRO');
            }
          }}
          onEdit={onEditKundali || onOpenBirthForm}
          lang={lang}
          hideWorkspaceSegmentedControl={menuLayoutMode === 'vertical'}
        />
      )}

      {/* Main Responsive Layout: Collapsible Sidebar (in Vertical Mode) + Canvas */}
      <div className="flex-1 flex relative overflow-hidden">
        {/* Left-Side Collapsible Navigation Sidebar (Rendered in Vertical Menu Mode) */}
        {menuLayoutMode === 'vertical' && (
          <CollapsibleSidebar
            activeWorkspaceMode={proWorkspaceTab}
            onSelectWorkspaceMode={(mode) => {
              setActiveRole('PRO');
              setProWorkspaceTab(mode as any);
            }}
            activeFeatureTab={activeTab}
            onSelectFeatureTab={(tabKey) => {
              setActiveRole('PRO');
              setProWorkspaceTab('tools');
              window.dispatchEvent(new CustomEvent('astro_navigate_tab', { detail: tabKey }));
            }}
            lang={lang}
            setLang={setLang}
            isOpenOnMobile={isSidebarOpenOnMobile}
            onCloseMobile={() => setIsSidebarOpenOnMobile(false)}
            onOpenPanchang={() => {
              setActiveRole('PRO');
              setProWorkspaceTab('tools');
              window.dispatchEvent(new CustomEvent('astro_navigate_tab', { detail: 'panchang' }));
            }}
            onOpenShubha={() => {
              window.dispatchEvent(new CustomEvent('astro_open_lucky_modal'));
            }}
            isCollapsed={isSidebarCollapsed}
            onToggleCollapse={toggleSidebarCollapse}
            onOpenBlueprint={() => setShowBlueprintModal(true)}
            onNewChart={onOpenBirthForm}
            onOpenVault={onOpenMyVault}
            onPrint={() => handleOpenPrintReport(activeRole === 'MANAGEMENT' ? 'PRO' : activeRole)}
            onOpenBTR={() => setIsBtrPromptOpen(true)}
            onOpenSettings={() => setIsSettingsModalOpen(true)}
            currentUser={currentUser}
            onOpenAdminPortal={onOpenAdminPortal}
            onOpenAuth={onOpenAuth}
            onLogout={onLogout}
          />
        )}

        {/* Main Scrollable Canvas */}
        <div className="flex-1 min-w-0 flex flex-col overflow-y-auto overflow-x-hidden">
          {/* ============================================================ */}
          {/* 3. MAIN WORKSPACE CONTENT CONTAINER                          */}
          {/* ============================================================ */}
          <main className="w-full px-4 sm:px-6 py-4 flex-1 space-y-6">
        {activeRole === 'BASIC' && (
          <BasicAnalysisView
            kundali={kundali}
            analysisResult={analysisResult}
            selectedThemeId={selectedThemeId}
            onSelectTheme={setSelectedThemeId}
            selectedQuestionId={selectedQuestionId}
            onSelectQuestion={setSelectedQuestionId}
            lang={lang}
            chartStyle={chartStyle}
            setChartStyle={setChartStyle}
            onOpenPrintReport={handleOpenPrintReport}
            onSwitchRole={isBasicUser ? undefined : () => setActiveRole('PRO')}
            onOpenAcharya={
              isBasicUser
                ? undefined
                : (q) => {
                    if (q) onCustomQuestionChange(q);
                    setActiveRole('ACHARYA');
                  }
            }
            onEditKundali={onEditKundali}
            onOpenBTR={() => setIsRectificationModalOpen(true)}
            isBasicUser={isBasicUser}
          />
        )}

        {activeRole === 'PRO' && (
          <>
            {proWorkspaceTab === 'enterprise-studio' && (
              <VedicMilestoneDestinyStudio
                kundali={kundali}
                lang={lang}
                chartStyle={chartStyle}
                onOpenConsultation={(prompt) => {
                  if (prompt) onCustomQuestionChange(prompt);
                  setActiveRole('ACHARYA');
                }}
              />
            )}

            {proWorkspaceTab === 'astrologer' && (
              <BasicAnalysisView
                kundali={kundali}
                analysisResult={analysisResult}
                selectedThemeId={selectedThemeId}
                onSelectTheme={setSelectedThemeId}
                selectedQuestionId={selectedQuestionId}
                onSelectQuestion={setSelectedQuestionId}
                lang={lang}
                chartStyle={chartStyle}
                setChartStyle={setChartStyle}
                onOpenPrintReport={handleOpenPrintReport}
                onSwitchRole={() => {}}
                onOpenAcharya={(q) => {
                  if (q) onCustomQuestionChange(q);
                  setActiveRole('ACHARYA');
                }}
                onEditKundali={onEditKundali}
                onOpenBTR={() => setIsRectificationModalOpen(true)}
                isBasicUser={false}
              />
            )}

            {(proWorkspaceTab === 'tools' || proWorkspaceTab === 'synthesis' || proWorkspaceTab === 'dossier') && (
              <ProAnalysisView
                kundali={kundali}
                analysisResult={analysisResult}
                selectedThemeId={selectedThemeId}
                onSelectTheme={setSelectedThemeId}
                selectedQuestionId={selectedQuestionId}
                onSelectQuestion={setSelectedQuestionId}
                lang={lang}
                chartStyle={chartStyle}
                setChartStyle={setChartStyle}
                onOpenReasoningModal={() => setIsReasoningModalOpen(true)}
                onOpenPrintReport={handleOpenPrintReport}
                onEditKundali={onEditKundali}
                aiQuota={aiQuota}
                customQuestionInput={customQuestionInput}
                onCustomQuestionChange={onCustomQuestionChange}
                onRequestAiAnalysis={onRequestAiAnalysis}
                isAiLoading={isAiLoading}
                aiInterpretation={aiInterpretation}
                aiError={aiError}
                proTab={proWorkspaceTab as any}
                onSelectProTab={setProWorkspaceTab as any}
              />
            )}
          </>
        )}

        {activeRole === 'ACHARYA' && (
          <AcharyaConsultationView
            kundali={kundali}
            analysisResult={analysisResult}
            selectedThemeId={selectedThemeId}
            onSelectTheme={setSelectedThemeId}
            selectedQuestionId={selectedQuestionId}
            onSelectQuestion={setSelectedQuestionId}
            lang={lang}
            chartStyle={chartStyle}
            setChartStyle={setChartStyle}
            onOpenPrintReport={handleOpenPrintReport}
            onEditKundali={onEditKundali}
            aiQuota={aiQuota}
            customQuestionInput={customQuestionInput}
            onCustomQuestionChange={onCustomQuestionChange}
            onRequestAiAnalysis={onRequestAiAnalysis}
            isAiLoading={isAiLoading}
            aiInterpretation={aiInterpretation}
            aiError={aiError}
          />
        )}

        {activeRole === 'MANAGEMENT' && (
          <ManagementWorkspace
            kundali={kundali}
            lang={lang}
            currentUser={currentUser}
            onOpenKundaliForClient={(client) => {
              // Load native client
              if (client.kundaliData) {
                onLoadSavedKundali(client.kundaliData);
              }
              setActiveRole('PRO');
            }}
            onOpenProAnalysis={(caseItem) => {
              const kData = (caseItem as any).kundaliData || kundali;
              if (kData) {
                onLoadSavedKundali(kData);
              }
              setActiveRole('PRO');
            }}
            onOpenAcharyaConsultation={(caseItem) => {
              const kData = (caseItem as any).kundaliData || kundali;
              if (kData) {
                onLoadSavedKundali(kData);
              }
              setActiveRole('ACHARYA');
            }}
            onEditKundali={onEditKundali}
          />
        )}
      </main>
        </div>
      </div>

      {/* ============================================================ */}
      {/* 4. MODALS & DIALOGS                                          */}
      {/* ============================================================ */}
      
      {/* Classical Parashari Reasoning Modal */}
      {isReasoningModalOpen && (
        <ClassicalReasoningModal
          isOpen={isReasoningModalOpen}
          onClose={() => setIsReasoningModalOpen(false)}
          analysisResult={analysisResult}
          lang={lang}
        />
      )}

      {/* Publication-Grade A4 Dossier Report Modal */}
      {isPrintModalOpen && (
        <PrintReportModal
          isOpen={isPrintModalOpen}
          onClose={() => setIsPrintModalOpen(false)}
          kundali={kundali}
          analysisResult={analysisResult}
          reportMode={printReportMode}
          lang={lang}
        />
      )}

      {/* Universal Settings Modal (Language, Chart Style, Calculation preferences, Menu Layout) */}
      <SettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
        lang={lang}
        setLang={setLang}
        chartStyle={chartStyle}
        setChartStyle={setChartStyle}
        menuLayoutMode={menuLayoutMode}
        onMenuLayoutModeChange={setMenuLayoutMode}
        onOpenLegacyDashboard={() => onOpenLegacyDashboard(activeRole)}
        onOpenVersionModal={() => setIsVersionModalOpen(true)}
        isSuperAdmin={isSuperAdmin}
      />

      {/* App Version & 6-Version Release History Modal (Triggered via Settings) */}
      <AppVersionModal
        isOpen={isVersionModalOpen}
        onClose={() => setIsVersionModalOpen(false)}
        lang={lang}
        savedKundalisCount={savedKundalis?.length || 0}
      />

      {/* Birth Time Rectification Modal */}
      <BirthTimeRectificationModal
        isOpen={isRectificationModalOpen}
        onClose={() => setIsRectificationModalOpen(false)}
        lang={lang}
        kundali={kundali}
        currentUser={currentUser}
        onApplyRectifiedKundali={onApplyRectifiedKundali || onLoadSavedKundali}
      />

      {/* BTR Prompt Choice Modal */}
      <BtrPromptModal
        isOpen={isBtrPromptOpen}
        onClose={() => setIsBtrPromptOpen(false)}
        lang={lang}
        currentKundali={kundali}
        onSelectCurrent={() => {
          setIsBtrPromptOpen(false);
          setIsRectificationModalOpen(true);
        }}
        onSelectExisting={() => {
          setIsBtrPromptOpen(false);
          onOpenMyVault();
        }}
        onSelectNew={() => {
          setIsBtrPromptOpen(false);
          onOpenBirthForm();
        }}
      />

      {/* Jataka Birth Blueprint Master Modal */}
      {showBlueprintModal && kundali && (
        <JatakaBirthBlueprintModal
          isOpen={showBlueprintModal}
          onClose={() => setShowBlueprintModal(false)}
          kundali={kundali}
          lang={lang as any}
        />
      )}

      {/* ============================================================ */}
      {/* 5. EDITORIAL FOOTER                                          */}
      {/* ============================================================ */}
      <footer className="bg-astro-bg border-t border-astro-border py-6 text-center text-xs text-astro-secondary-text mt-auto">
        <div className="w-full px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <SwastikLogo size="sm" />
            <span className="font-serif font-bold text-astro-text">VEDANG JYOTISHAM {isSuperAdmin ? APP_VERSION : ''}</span>
            <span>•</span>
            <span>{isHi ? 'शास्त्रीय एवं व्यावहारिक ज्योतिष मंच' : 'Vedic Astrological Intelligence & Research Studio'}</span>
          </div>

          <div className="flex items-center gap-4">
            {isSuperAdmin && (
              <button
                onClick={() => onOpenLegacyDashboard(activeRole)}
                className="text-astro-secondary-text hover:text-astro-primary font-semibold flex items-center gap-1 transition-colors cursor-pointer"
              >
                <RotateCcw className="w-3 h-3 text-astro-accent" />
                <span>{isHi ? 'लेगेसी डैशबोर्ड पर जाएं' : 'Switch to Legacy Dashboard'}</span>
              </button>
            )}
            <span className="text-astro-accent font-serif">ॐ शान्तिः शान्तिः शान्तिः ॐ</span>
          </div>
        </div>
      </footer>
    </div>
  );
};
